const STATE_KEY = "hydrotools:fmgt-controller:v1";

function esc(v) {
  return String(v ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function readState() {
  const fallback = {
    executablePath: "",
    projectTemplatePath: "",
    gsfFolder: "",
    outputFolder: "",
    mosaicName: "Backscatter_Mosaic",
    expectedGeotiffName: "",
    launchArgsTemplate: "",
    overwrite: true,
    dryRun: true,
    waitForExit: true,
    requireGeotiff: true,
    projectAction: "none",
    projectName: "",
    projectLocation: "",
    projectOpenFolder: "",
    enableKeystrokeAutomation: false,
    automationInitialDelayMs: 2500,
    keystrokeScript: "",
    autoRunNext: true,
  };
  try {
    const raw = localStorage.getItem(STATE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return { ...fallback, ...(parsed && typeof parsed === "object" ? parsed : {}) };
  } catch {
    return fallback;
  }
}

function writeState(state) {
  try { localStorage.setItem(STATE_KEY, JSON.stringify(state)); } catch {}
}

async function requestJson(url, options = {}) {
  const response = await fetch(url, {
    method: options.method || "GET",
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
    body: options.body == null ? undefined : JSON.stringify(options.body),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(String(payload?.error || `Request failed (${response.status})`));
  return payload;
}

export const fmgtControllerTool = {
  id: "fmgt-controller",
  name: "FMGT Controller",
  init(app) { this._rt = this._rt ?? createRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  renderPanel(app, el) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.mount(el);
  },
  serialize() { return this._rt?.snapshot?.() ?? null; },
  restore(app, data) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.restore?.(data);
  },
};

function createRuntime(app) {
  let containerEl = null;
  let refreshTimer = null;
  let busy = false;
  let state = readState();
  let status = "";
  let serverStatus = {
    queue: [],
    recent: [],
    active: null,
    settings: null,
    lastUpdatedAt: "",
  };

  function persist() {
    writeState(state);
  }

  function pullStateFromForm() {
    if (!containerEl) return;
    state.executablePath = String(containerEl.querySelector("#fmgt-exe")?.value || "").trim();
    state.projectTemplatePath = String(containerEl.querySelector("#fmgt-template")?.value || "").trim();
    state.gsfFolder = String(containerEl.querySelector("#fmgt-gsf-folder")?.value || "").trim();
    state.outputFolder = String(containerEl.querySelector("#fmgt-output-folder")?.value || "").trim();
    state.mosaicName = String(containerEl.querySelector("#fmgt-mosaic-name")?.value || "Backscatter_Mosaic").trim();
    state.expectedGeotiffName = String(containerEl.querySelector("#fmgt-expected-geotiff")?.value || "").trim();
    state.launchArgsTemplate = String(containerEl.querySelector("#fmgt-launch-args")?.value || "").trim();
    state.overwrite = containerEl.querySelector("#fmgt-overwrite")?.checked === true;
    state.dryRun = containerEl.querySelector("#fmgt-dryrun")?.checked !== false;
    state.waitForExit = containerEl.querySelector("#fmgt-wait-exit")?.checked === true;
    state.requireGeotiff = containerEl.querySelector("#fmgt-require-geotiff")?.checked !== false;
    state.projectAction = String(containerEl.querySelector("#fmgt-project-action")?.value || "none").trim().toLowerCase();
    state.projectName = String(containerEl.querySelector("#fmgt-project-name")?.value || "").trim();
    state.projectLocation = String(containerEl.querySelector("#fmgt-project-location")?.value || "").trim();
    state.projectOpenFolder = String(containerEl.querySelector("#fmgt-project-open-folder")?.value || "").trim();
    state.enableKeystrokeAutomation = containerEl.querySelector("#fmgt-automation-enabled")?.checked === true;
    state.automationInitialDelayMs = Math.max(0, Number(containerEl.querySelector("#fmgt-automation-delay")?.value || 2500) || 2500);
    state.keystrokeScript = String(containerEl.querySelector("#fmgt-keystrokes")?.value || "").trim();
    state.autoRunNext = containerEl.querySelector("#fmgt-auto-run-next")?.checked !== false;
    persist();
  }

  function formatTime(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    return Number.isFinite(d.getTime()) ? d.toLocaleString() : String(iso);
  }

  function render() {
    if (!containerEl) return;
    const queue = Array.isArray(serverStatus.queue) ? serverStatus.queue : [];
    const recent = Array.isArray(serverStatus.recent) ? serverStatus.recent : [];
    const active = serverStatus.active || null;
    containerEl.innerHTML = `
      <div class="card" style="padding:10px;">
        <div class="lm-title">FMGT Controller</div>
        <div class="small" style="margin-top:6px;">Queue and supervise FMGT backscatter mosaic jobs after Qimera GSF export.</div>

        <details class="toolset-group" open style="margin-top:10px;">
          <summary>Job Defaults</summary>
          <div class="toolset-content">
            <div class="row lm-add-row" style="margin-top:6px;">
              <label class="small" style="min-width:130px;">FMGT Executable</label>
              <input id="fmgt-exe" type="text" value="${esc(state.executablePath)}" placeholder="C:\\Program Files\\QPS\\FMGT\\bin\\fmgt.exe" style="flex:1;" />
            </div>
            <div class="row lm-add-row" style="margin-top:6px;">
              <label class="small" style="min-width:130px;">Template Project</label>
              <input id="fmgt-template" type="text" value="${esc(state.projectTemplatePath)}" placeholder="C:\\FMGT\\Templates\\default.fmgtproj" style="flex:1;" />
            </div>
            <div class="row lm-add-row" style="margin-top:6px;">
              <label class="small" style="min-width:130px;">GSF Folder</label>
              <input id="fmgt-gsf-folder" type="text" value="${esc(state.gsfFolder)}" placeholder="C:\\QimeraProjects\\TEST_8\\exports\\gsf" style="flex:1;" />
            </div>
            <div class="row lm-add-row" style="margin-top:6px;">
              <label class="small" style="min-width:130px;">Output Folder</label>
              <input id="fmgt-output-folder" type="text" value="${esc(state.outputFolder)}" placeholder="C:\\FMGT\\Output" style="flex:1;" />
            </div>
            <div class="row lm-add-row" style="margin-top:6px;">
              <label class="small" style="min-width:130px;">Mosaic Name</label>
              <input id="fmgt-mosaic-name" type="text" value="${esc(state.mosaicName)}" placeholder="Backscatter_Mosaic" style="width:260px;" />
            </div>
            <div class="row lm-add-row" style="margin-top:6px;">
              <label class="small" style="min-width:130px;">Expected GeoTIFF</label>
              <input id="fmgt-expected-geotiff" type="text" value="${esc(state.expectedGeotiffName)}" placeholder="optional: Backscatter_Mosaic.tif" style="flex:1;" />
            </div>
            <div class="row lm-add-row" style="margin-top:6px;">
              <label class="small" style="min-width:130px;">Launch Args</label>
              <input id="fmgt-launch-args" type="text" value="${esc(state.launchArgsTemplate)}" placeholder="Example: --project \"{projectTemplatePath}\" --input \"{gsfFolder}\" --output \"{outputFolder}\" --name \"{mosaicName}\"" style="flex:1;" />
            </div>
            <div class="small" style="margin-top:6px; opacity:.8;">
              Placeholders: <code>{projectTemplatePath}</code>, <code>{gsfFolder}</code>, <code>{outputFolder}</code>, <code>{mosaicName}</code>, <code>{projectName}</code>, <code>{projectLocation}</code>, <code>{projectOpenFolder}</code>, <code>{jobId}</code>
            </div>
            <div class="row lm-add-row" style="margin-top:8px; gap:14px;">
              <label class="small"><input id="fmgt-overwrite" type="checkbox" ${state.overwrite ? "checked" : ""} /> Overwrite output</label>
              <label class="small"><input id="fmgt-dryrun" type="checkbox" ${state.dryRun ? "checked" : ""} /> Dry-run job</label>
              <label class="small"><input id="fmgt-wait-exit" type="checkbox" ${state.waitForExit ? "checked" : ""} /> Wait for FMGT exit</label>
              <label class="small"><input id="fmgt-require-geotiff" type="checkbox" ${state.requireGeotiff ? "checked" : ""} /> Require GeoTIFF output</label>
              <label class="small"><input id="fmgt-auto-run-next" type="checkbox" ${state.autoRunNext ? "checked" : ""} /> Auto run next after queue</label>
            </div>
            <details class="toolset-group" style="margin-top:8px;" open>
              <summary>Project Setup</summary>
              <div class="toolset-content">
                <div class="row lm-add-row" style="margin-top:6px;">
                  <label class="small" style="min-width:130px;">Action</label>
                  <select id="fmgt-project-action" style="width:220px;">
                    <option value="none" ${state.projectAction === "none" ? "selected" : ""}>None</option>
                    <option value="create" ${state.projectAction === "create" ? "selected" : ""}>Create New Project</option>
                    <option value="open" ${state.projectAction === "open" ? "selected" : ""}>Open Existing Project</option>
                  </select>
                </div>
                <div class="row lm-add-row" style="margin-top:6px;">
                  <label class="small" style="min-width:130px;">Project Name</label>
                  <input id="fmgt-project-name" type="text" value="${esc(state.projectName)}" placeholder="Used by Create New Project" style="flex:1;" />
                </div>
                <div class="row lm-add-row" style="margin-top:6px;">
                  <label class="small" style="min-width:130px;">Project Location</label>
                  <input id="fmgt-project-location" type="text" value="${esc(state.projectLocation)}" placeholder="Used by Create New Project" style="flex:1;" />
                </div>
                <div class="row lm-add-row" style="margin-top:6px;">
                  <label class="small" style="min-width:130px;">Open Folder</label>
                  <input id="fmgt-project-open-folder" type="text" value="${esc(state.projectOpenFolder)}" placeholder="Used by Open Existing Project" style="flex:1;" />
                </div>
                <div class="small" style="margin-top:6px; opacity:.8;">Sequencing: project setup runs first after FMGT launch, then custom keystrokes run.</div>
              </div>
            </details>
            <details class="toolset-group" style="margin-top:8px;">
              <summary>Keystroke Automation</summary>
              <div class="toolset-content">
                <div class="row lm-add-row" style="margin-top:6px; gap:14px;">
                  <label class="small"><input id="fmgt-automation-enabled" type="checkbox" ${state.enableKeystrokeAutomation ? "checked" : ""} /> Enable keystroke automation</label>
                </div>
                <div class="row lm-add-row" style="margin-top:6px;">
                  <label class="small" style="min-width:130px;">Initial Delay (ms)</label>
                  <input id="fmgt-automation-delay" type="number" min="0" step="100" value="${esc(String(state.automationInitialDelayMs || 2500))}" style="width:140px;" />
                </div>
                <div class="small" style="margin-top:6px; opacity:.8;">Commands per line: <code>WAIT 1000</code>, <code>KEY ALT+F</code>, <code>KEY ENTER</code>, <code>TEXT C:\\path\\file.gsf</code>, <code>RAW ^v</code>, <code>ACTIVATE</code></div>
                <textarea id="fmgt-keystrokes" rows="8" style="width:100%; margin-top:6px; font-family:Consolas, monospace; font-size:12px;" placeholder="Example:\nWAIT 1200\nACTIVATE\nKEY ALT+F\nKEY I\nWAIT 500\nTEXT {gsfFolder}\nKEY ENTER">${esc(state.keystrokeScript)}</textarea>
              </div>
            </details>
            <div class="row lm-add-row" style="margin-top:8px;">
              <button id="fmgt-discover" class="btn">Discover FMGT</button>
              <button id="fmgt-save-defaults" class="btn">Save Defaults</button>
              <button id="fmgt-queue-job" class="btn primary">Queue Mosaic Job</button>
              <button id="fmgt-run-next" class="btn">Run Next</button>
              <button id="fmgt-cancel-active" class="btn">Cancel Active</button>
              <button id="fmgt-refresh" class="btn">Refresh</button>
            </div>
          </div>
        </details>

        <details class="toolset-group" open style="margin-top:10px;">
          <summary>Controller Status</summary>
          <div class="toolset-content">
            <div class="small" style="margin-top:4px; opacity:.85;">Active job: ${esc(active?.id || "none")}${active?.state ? ` (${esc(active.state)})` : ""}</div>
            <div class="small" style="margin-top:4px; opacity:.85;">Queue: ${queue.length} pending | Recent: ${recent.length} | Last update: ${esc(formatTime(serverStatus.lastUpdatedAt) || "n/a")}</div>
            <div class="small" style="margin-top:4px; opacity:.85;">Resolved EXE: ${esc(serverStatus?.settings?.executablePath || state.executablePath || "(not set)")}</div>
            ${!active && queue.length > 0 ? `<div class="small" style="margin-top:4px; color:#fbbf24;">Queue is waiting. Click Run Next, or enable Auto run next after queue.</div>` : ""}
            <div class="small" style="margin-top:6px; color:${status ? "#cbd5e1" : "#94a3b8"};">${esc(status || "Ready.")}</div>
          </div>
        </details>

        <details class="toolset-group" open style="margin-top:10px;">
          <summary>Jobs</summary>
          <div class="toolset-content" style="max-height:280px; overflow:auto;">
            <table style="width:100%; border-collapse:collapse; font-size:12px;">
              <thead>
                <tr style="text-align:left; border-bottom:1px solid rgba(148,163,184,.18);">
                  <th style="padding:6px 8px;">Job ID</th>
                  <th style="padding:6px 8px;">State</th>
                  <th style="padding:6px 8px;">Mosaic</th>
                  <th style="padding:6px 8px;">Last Log</th>
                  <th style="padding:6px 8px;">Created</th>
                </tr>
              </thead>
              <tbody>
                ${[...(active ? [active] : []), ...queue, ...recent].slice(0, 60).map((job) => `
                  <tr style="border-bottom:1px solid rgba(148,163,184,.12);">
                    <td style="padding:6px 8px;"><code>${esc(job.id || "")}</code></td>
                    <td style="padding:6px 8px;">${esc(job.state || "")}</td>
                    <td style="padding:6px 8px;">${esc(job?.request?.mosaicName || "")}</td>
                    <td style="padding:6px 8px;">${esc(Array.isArray(job?.logs) && job.logs.length ? job.logs[job.logs.length - 1]?.message || "" : "")}</td>
                    <td style="padding:6px 8px;">${esc(formatTime(job.createdAt) || "")}</td>
                  </tr>
                `).join("") || `<tr><td colspan="5" style="padding:8px;" class="small">No jobs yet.</td></tr>`}
              </tbody>
            </table>
          </div>
        </details>
      </div>
    `;
  }

  async function refreshStatus({ quiet = false } = {}) {
    try {
      const payload = await requestJson("/api/fmgt/status");
      serverStatus = {
        queue: Array.isArray(payload?.queue) ? payload.queue : [],
        recent: Array.isArray(payload?.recent) ? payload.recent : [],
        active: payload?.active || null,
        settings: payload?.settings || null,
        lastUpdatedAt: payload?.lastUpdatedAt || new Date().toISOString(),
      };
      if (!quiet) status = "FMGT controller status refreshed.";
    } catch (error) {
      status = error?.message || "Could not read FMGT controller status.";
    } finally {
      render();
    }
  }

  async function saveDefaults() {
    pullStateFromForm();
    busy = true;
    try {
      await requestJson("/api/fmgt/settings", {
        method: "POST",
        body: {
          executablePath: state.executablePath,
          projectTemplatePath: state.projectTemplatePath,
          gsfFolder: state.gsfFolder,
          outputFolder: state.outputFolder,
          mosaicName: state.mosaicName,
          expectedGeotiffName: state.expectedGeotiffName,
          launchArgsTemplate: state.launchArgsTemplate,
          overwrite: state.overwrite,
          dryRun: state.dryRun,
          waitForExit: state.waitForExit,
          requireGeotiff: state.requireGeotiff,
          projectAction: state.projectAction,
          projectName: state.projectName,
          projectLocation: state.projectLocation,
          projectOpenFolder: state.projectOpenFolder,
          enableKeystrokeAutomation: state.enableKeystrokeAutomation,
          automationInitialDelayMs: state.automationInitialDelayMs,
          keystrokeScript: state.keystrokeScript,
        },
      });
      status = "Saved FMGT controller defaults.";
      await refreshStatus({ quiet: true });
    } catch (error) {
      status = error?.message || "Could not save defaults.";
      render();
    } finally {
      busy = false;
    }
  }

  async function queueJob() {
    pullStateFromForm();
    busy = true;
    try {
      const payload = await requestJson("/api/fmgt/jobs", {
        method: "POST",
        body: {
          executablePath: state.executablePath,
          projectTemplatePath: state.projectTemplatePath,
          gsfFolder: state.gsfFolder,
          outputFolder: state.outputFolder,
          mosaicName: state.mosaicName,
          expectedGeotiffName: state.expectedGeotiffName,
          launchArgsTemplate: state.launchArgsTemplate,
          overwrite: state.overwrite,
          dryRun: state.dryRun,
          waitForExit: state.waitForExit,
          requireGeotiff: state.requireGeotiff,
          projectAction: state.projectAction,
          projectName: state.projectName,
          projectLocation: state.projectLocation,
          projectOpenFolder: state.projectOpenFolder,
          enableKeystrokeAutomation: state.enableKeystrokeAutomation,
          automationInitialDelayMs: state.automationInitialDelayMs,
          keystrokeScript: state.keystrokeScript,
        },
      });
      status = `Queued FMGT job ${payload?.job?.id || ""}.`;
      if (state.autoRunNext) {
        await requestJson("/api/fmgt/run-next", { method: "POST" });
        status = `Queued and started FMGT job ${payload?.job?.id || ""}.`;
      } else {
        status = `Queued FMGT job ${payload?.job?.id || ""}. Click Run Next to start, or enable Auto run next after queue.`;
      }
      await refreshStatus({ quiet: true });
    } catch (error) {
      status = error?.message || "Could not queue FMGT job.";
      render();
    } finally {
      busy = false;
    }
  }

  async function runNextJob() {
    busy = true;
    try {
      await requestJson("/api/fmgt/run-next", { method: "POST" });
      status = "Triggered FMGT run-next.";
      await refreshStatus({ quiet: true });
    } catch (error) {
      status = error?.message || "Could not run next FMGT job.";
      render();
    } finally {
      busy = false;
    }
  }

  async function cancelActiveJob() {
    busy = true;
    try {
      await requestJson("/api/fmgt/cancel-active", { method: "POST" });
      status = "Cancel requested for active FMGT job.";
      await refreshStatus({ quiet: true });
    } catch (error) {
      status = error?.message || "Could not cancel active FMGT job.";
      render();
    } finally {
      busy = false;
    }
  }

  async function discoverFmgt() {
    busy = true;
    try {
      const payload = await requestJson("/api/fmgt/discover");
      if (payload?.executablePath) {
        state.executablePath = String(payload.executablePath);
        persist();
      }
      status = payload?.executablePath
        ? `Found FMGT executable: ${payload.executablePath}`
        : "No FMGT executable discovered automatically.";
      await refreshStatus({ quiet: true });
    } catch (error) {
      status = error?.message || "Could not discover FMGT installation.";
      render();
    } finally {
      busy = false;
    }
  }

  function onInput() {
    if (busy) return;
    pullStateFromForm();
  }

  function onClick(e) {
    if (e.target?.closest?.("#fmgt-discover")) {
      discoverFmgt();
      return;
    }
    if (e.target?.closest?.("#fmgt-save-defaults")) {
      saveDefaults();
      return;
    }
    if (e.target?.closest?.("#fmgt-queue-job")) {
      queueJob();
      return;
    }
    if (e.target?.closest?.("#fmgt-run-next")) {
      runNextJob();
      return;
    }
    if (e.target?.closest?.("#fmgt-cancel-active")) {
      cancelActiveJob();
      return;
    }
    if (e.target?.closest?.("#fmgt-refresh")) {
      refreshStatus();
    }
  }

  function startPolling() {
    if (refreshTimer) clearInterval(refreshTimer);
    refreshTimer = setInterval(() => {
      refreshStatus({ quiet: true });
    }, 3500);
  }

  function stopPolling() {
    if (refreshTimer) {
      clearInterval(refreshTimer);
      refreshTimer = null;
    }
  }

  function mount(el) {
    if (containerEl) {
      containerEl.removeEventListener("input", onInput);
      containerEl.removeEventListener("click", onClick);
    }
    containerEl = el;
    render();
    containerEl.addEventListener("input", onInput);
    containerEl.addEventListener("click", onClick);
    refreshStatus({ quiet: true });
    startPolling();
  }

  function destroy() {
    stopPolling();
    if (!containerEl) return;
    containerEl.removeEventListener("input", onInput);
    containerEl.removeEventListener("click", onClick);
    containerEl = null;
  }

  function snapshot() {
    return { version: 1, state: { ...state } };
  }

  function restore(data) {
    const s = data?.state;
    if (!s || typeof s !== "object") return;
    state = {
      ...state,
      ...s,
      expectedGeotiffName: String(s.expectedGeotiffName || ""),
      launchArgsTemplate: String(s.launchArgsTemplate || ""),
      overwrite: s.overwrite !== false,
      dryRun: s.dryRun !== false,
      waitForExit: s.waitForExit === true,
      requireGeotiff: s.requireGeotiff !== false,
      projectAction: ["none", "create", "open"].includes(String(s.projectAction || "").toLowerCase()) ? String(s.projectAction || "").toLowerCase() : "none",
      projectName: String(s.projectName || ""),
      projectLocation: String(s.projectLocation || ""),
      projectOpenFolder: String(s.projectOpenFolder || ""),
      enableKeystrokeAutomation: s.enableKeystrokeAutomation === true,
      automationInitialDelayMs: Math.max(0, Number(s.automationInitialDelayMs || 2500) || 2500),
      keystrokeScript: String(s.keystrokeScript || ""),
      autoRunNext: s.autoRunNext !== false,
    };
    persist();
    render();
  }

  return { mount, destroy, snapshot, restore };
}
